import { mat4 } from 'gl-matrix';
import { Camera } from '../camera';
import { DirectionalLight } from '../light';
import { RenderSettings } from '../renderer';
import { Texture } from '../texture';

/**
 * Structural type covering the lighting/CSM/volumetric uniform setters that
 * `ForwardTransparentShader` and `LiquidSurfaceShader` both expose. Lets a
 * single helper bind the shared transparent-pass uniforms regardless of which
 * shader is active.
 */
export interface TransparentLightingUniforms {
  setLightDataMap(texture: Texture): TransparentLightingUniforms;
  setLightCount(value: number): TransparentLightingUniforms;
  setLightDataHeight(value: number): TransparentLightingUniforms;
  setTileDataMap(texture: Texture): TransparentLightingUniforms;
  setTilesX(value: number): TransparentLightingUniforms;
  setTileSize(value: number): TransparentLightingUniforms;

  setShadowCascadeMap0(texture: Texture): TransparentLightingUniforms;
  setShadowCascadeMap1(texture: Texture): TransparentLightingUniforms;
  setShadowCascadeMap2(texture: Texture): TransparentLightingUniforms;
  setShadowCascadeMap3(texture: Texture): TransparentLightingUniforms;
  setCascadeViewMatrix(index: number, value: mat4 | number[]): TransparentLightingUniforms;
  setCascadeSplits(value: number[] | Float32Array): TransparentLightingUniforms;
  setViewMatrix(value: mat4 | number[]): TransparentLightingUniforms;

  setVolumetricMap(texture: Texture): TransparentLightingUniforms;
  setScreenSize(value: number[] | Float32Array): TransparentLightingUniforms;
}

/**
 * Find the (single) directional light with a CSM attached, if any. Both the
 * forward-transparent and liquid-surface shaders only ever bind one cascade
 * source per draw.
 */
export function findCsmLight(settings: RenderSettings): DirectionalLight | undefined {
  return settings.lights.find(
    (l): l is DirectionalLight => l instanceof DirectionalLight && l.cascadedShadowMap != null,
  );
}

/**
 * Bind the lighting block shared by every transparent-pass shader: light data
 * texture, CSM cascades, and the volumetric fog texture. Uniformless slots are
 * cleared so the shader doesn't read stale state from a prior draw.
 */
export function bindTransparentLighting(
  uniforms: TransparentLightingUniforms,
  settings: RenderSettings,
  camera: Camera,
  csmLight: DirectionalLight | undefined,
): void {
  const ldt = settings.lightDataTexture;
  if (ldt && ldt.count > 0) {
    uniforms.setLightDataMap(ldt.texture).setLightCount(ldt.count).setLightDataHeight(ldt.capacity);
  } else {
    uniforms.setLightCount(0);
  }

  if (settings.tileDataTexture && settings.tilesX != null) {
    uniforms
      .setTileDataMap(settings.tileDataTexture.texture)
      .setTilesX(settings.tilesX)
      .setTileSize(settings.tileSize ?? 16);
  }

  if (csmLight) {
    const csm = csmLight.cascadedShadowMap!;
    uniforms
      .setShadowCascadeMap0(csm.momentsTextures[0])
      .setShadowCascadeMap1(csm.momentsTextures[1])
      .setShadowCascadeMap2(csm.momentsTextures[2])
      .setShadowCascadeMap3(csm.momentsTextures[3])
      .setCascadeViewMatrix(0, csm.cascadeViewMatrices[0])
      .setCascadeViewMatrix(1, csm.cascadeViewMatrices[1])
      .setCascadeViewMatrix(2, csm.cascadeViewMatrices[2])
      .setCascadeViewMatrix(3, csm.cascadeViewMatrices[3])
      .setCascadeSplits(csm.cascadeSplits)
      .setViewMatrix(camera.modelViewMatrix);
  }

  if (settings.volumetricTexture && settings.screenSize) {
    uniforms.setVolumetricMap(settings.volumetricTexture).setScreenSize(settings.screenSize);
  }
}
